import { GameEndpoints } from "./api/GameEndpoints";
import {
  PendingRequest,
  RunAckPayload,
  RunStateWithTimers,
  browserIsOffline,
  digestOperation,
  hexBytes,
  isIdempotentMethod,
  isRunAckPayload,
  isRunOperationMethod,
  predictionAction,
  randomUUID,
  wait,
} from "./api/protocol";
import { normalizeRunView } from "./combatState";
import { SecureSocket, type SecureEnvelope } from "./secureTransport";
import { SynchronizedServerClock } from "./serverClock";
import type { RaceView, RoomView, RunView, Session, User } from "./types";
import { APIError } from "./types";
import {
  advanceRunMachine,
  canPredictRun,
  confirmRunMachine,
  predictRunOperation,
  releaseRunMachine,
  rollbackRunMachine,
} from "./wasm";

const SESSION_KEY = "breacher.session.v1";
const RECONNECT_BASE_DELAY_MS = 800;
const RECONNECT_MAX_DELAY_MS = 30_000;
const AUTOMATIC_RECONNECT_LIMIT = 1;

export type ConnectionState =
  | "disconnected"
  | "reconnecting"
  | "retrying"
  | "connected"
  | "offline";

export function reconnectDelayMs(attempt: number, randomValue = Math.random()): number {
  const cap = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_BASE_DELAY_MS * 2 ** Math.min(Math.max(0, attempt), 6),
  );
  const unit = Number.isFinite(randomValue) ? Math.min(1, Math.max(0, randomValue)) : 0.5;
  return Math.round(cap / 2 + (cap / 2) * unit);
}

export class APIClient extends GameEndpoints {
  private session: Session | null = null;
  private listener: (session: Session | null) => void;
  private socket: SecureSocket | null = null;
  private connecting: Promise<SecureSocket> | null = null;
  private bootstrapping: Promise<Session | null> | null = null;
  private pending = new Map<string, PendingRequest>();
  private nextID = 1;
  private runListener: ((view: RunView) => void) | null = null;
  private roomListener: ((view: RoomView) => void) | null = null;
  private raceListener: ((view: RaceView) => void) | null = null;
  private connectionListener: ((state: ConnectionState) => void) | null = null;
  private connectionState: ConnectionState = "disconnected";
  private reconnectTimer = 0;
  private automaticReconnectAttempts = 0;
  private reconnecting: Promise<void> | null = null;
  private restoringSession = false;
  private authenticatedSocket: SecureSocket | null = null;
  private authenticating: Promise<void> | null = null;
  private refreshingSession: Promise<Session> | null = null;
  private serverClock = new SynchronizedServerClock();
  private optimisticOperations = new Map<string, { method: string; startedAt: number }>();
  private lastAuthoritativeAt = performance.now();
  private runCursor: {
    runId: string;
    operationSeq: number;
    stateHash: string;
    revision: number;
  } | null = null;
  private confirmedRun: RunView | null = null;
  private operationDigest = "";
  private predictionTimer = 0;
  constructor(listener: (session: Session | null) => void) {
    super();
    this.listener = listener;
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        this.session = JSON.parse(stored) as Session;
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
    if (typeof window !== "undefined")
      window.addEventListener("offline", () => this.handleBrowserOffline());
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => this.handleVisibilityChange());
      window.addEventListener("pageshow", () => this.handleVisibilityChange());
    }
  }

  serverNow = (): number => this.serverClock.now();

  private handleBrowserOffline(): void {
    if (!this.session || this.connectionState === "offline") return;
    this.setConnectionState("reconnecting");
    if (this.socket?.isOpen) {
      this.socket.close();
      this.scheduleReconnect();
      return;
    }
    this.scheduleReconnect();
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState !== "visible") return;
    if (this.socket?.isOpen && this.authenticatedSocket === this.socket) {
      void this.syncTime().catch(() => undefined);
      return;
    }
    if (!this.session || this.reconnecting) return;
    this.automaticReconnectAttempts = 0;
    this.setConnectionState("reconnecting");
    void this.recoverAfterReconnect().catch(() => undefined);
  }

  onRunState(listener: (view: RunView) => void): () => void {
    this.runListener = listener;
    return () => {
      if (this.runListener === listener) this.runListener = null;
    };
  }

  onConnection(listener: (state: ConnectionState) => void): () => void {
    this.connectionListener = listener;
    listener(this.connectionState);
    return () => {
      if (this.connectionListener === listener) this.connectionListener = null;
    };
  }

  onRoomState(listener: (view: RoomView) => void): () => void {
    this.roomListener = listener;
    return () => {
      if (this.roomListener === listener) this.roomListener = null;
    };
  }

  onRaceState(listener: (view: RaceView) => void): () => void {
    this.raceListener = listener;
    return () => {
      if (this.raceListener === listener) this.raceListener = null;
    };
  }

  bootstrap(): Promise<Session | null> {
    if (this.bootstrapping) return this.bootstrapping;
    const operation = this.bootstrapOnce();
    this.bootstrapping = operation;
    const clear = () => {
      if (this.bootstrapping === operation) this.bootstrapping = null;
    };
    void operation.then(clear, clear);
    return operation;
  }

  private async bootstrapOnce(): Promise<Session | null> {
    if (!this.session?.refreshToken) return null;
    this.restoringSession = true;
    this.setConnectionState("reconnecting");
    try {
      for (let attempt = 0; attempt <= AUTOMATIC_RECONNECT_LIMIT; attempt++) {
        if (attempt > 0) {
          this.automaticReconnectAttempts = attempt;
          await wait(reconnectDelayMs(attempt - 1));
        }
        try {
          if (browserIsOffline()) throw new APIError("browser_offline", "当前网络不可用", 503);
          await this.ensureConnected();
          await this.ensureAuthenticated();
          await this.syncTime().catch(() => this.serverClock.now());
          if (!this.session) return null;
          this.setSession(this.session);
          this.automaticReconnectAttempts = 0;
          this.setConnectionState("connected");
          return this.session;
        } catch (error) {
          if (
            error instanceof APIError &&
            ["invalid_refresh", "unauthorized"].includes(error.code) &&
            !this.session
          )
            return null;
          if (attempt === AUTOMATIC_RECONNECT_LIMIT) {
            this.enterOffline();
            return null;
          }
        }
      }
    } finally {
      this.restoringSession = false;
    }
    return null;
  }

  async retryConnection(): Promise<void> {
    if (!this.session) throw new APIError("no_active_session", "没有可恢复的会话", 401);
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = 0;
    }
    this.automaticReconnectAttempts = AUTOMATIC_RECONNECT_LIMIT;
    this.setConnectionState("retrying");
    try {
      await this.recoverAfterReconnect();
    } catch (error) {
      throw error instanceof Error ? error : new APIError("connection_failed", "无法重新连接", 503);
    }
  }

  async login(username: string, password: string): Promise<Session> {
    return this.acceptSession(await this.send<Session>("auth.login", { username, password }));
  }

  async register(
    username: string,
    password: string,
    passwordConfirm: string,
    nickname: string,
  ): Promise<Session> {
    return this.acceptSession(
      await this.send<Session>("auth.register", { username, password, passwordConfirm, nickname }),
    );
  }

  async bind(
    username: string,
    password: string,
    passwordConfirm: string,
    nickname: string,
  ): Promise<Session> {
    return this.acceptSession(
      await this.request<Session>("auth.bind", { username, password, passwordConfirm, nickname }),
    );
  }

  async logout(): Promise<void> {
    if (this.session?.refreshToken) {
      await this.send("auth.logout", { refreshToken: this.session.refreshToken }).catch(
        () => undefined,
      );
    }
    this.setSession(null);
  }

  async deleteAccount(): Promise<void> {
    await this.request("auth.delete", { confirmation: "DELETE" });
    this.setSession(null);
  }

  async updateNickname(nickname: string): Promise<User> {
    const user = await this.request<User>("auth.nickname", { nickname });
    if (this.session) this.setSession({ ...this.session, user });
    return user;
  }

  async changePassword(
    currentPassword: string,
    password: string,
    passwordConfirm: string,
  ): Promise<Session> {
    return this.acceptSession(
      await this.request<Session>("auth.password", { currentPassword, password, passwordConfirm }),
    );
  }

  async syncTime(): Promise<number> {
    const startedAt = performance.now();
    const result = await this.request<{ serverNow: number }>("time.now");
    this.serverClock.synchronize(result.serverNow, performance.now() - startedAt);
    return this.serverClock.now();
  }

  async currentRun(): Promise<RunView> {
    return this.request<RunView>("run.current");
  }

  async resyncRun(): Promise<RunView> {
    return this.request<RunView>("run.resync");
  }

  async resumeRun(): Promise<RunView> {
    return this.request<RunView>("run.resume");
  }

  async startRun(shaftDepth = 1, useFreeSkip = false): Promise<RunView> {
    return this.request<RunView>("run.start", { shaftDepth, useFreeSkip });
  }

  async slide(
    axis: "row" | "col",
    index: number,
    distance: number,
    boardHash: string,
    automated = false,
  ): Promise<RunView> {
    await this.waitForRunUnlock();
    try {
      if (!canPredictRun()) {
        const preview = await this.request<{ matches: boolean }>("run.preview", {
          axis,
          index,
          distance,
          boardHash: this.runCursor?.stateHash ?? boardHash,
        });
        if (!preview.matches) throw new APIError("invalid_move", "本次滑动未形成消除", 400);
      }
      return await this.request<RunView>("run.slide", {
        axis,
        index,
        distance,
        boardHash: this.runCursor?.stateHash ?? boardHash,
        automated,
      });
    } catch (error) {
      if (error instanceof APIError && error.code === "state_mismatch") return this.resyncRun();
      throw error;
    }
  }

  async lockRow(row: number): Promise<RunView> {
    return this.requestRunWithResync("run.lock_row", { row });
  }

  async skill(skill: string): Promise<RunView> {
    await this.waitForRunUnlock();
    return this.requestRunWithResync("run.skill", { skill });
  }

  async chest(open: boolean): Promise<RunView> {
    return this.requestRunWithResync("run.chest", { open });
  }

  async retreat(): Promise<RunView> {
    return this.requestRunWithResync("run.retreat", {});
  }

  private refresh(): Promise<Session> {
    if (this.refreshingSession) return this.refreshingSession;
    const operation = (async () => {
      const session = await this.rotateSession();
      await this.ensureAuthenticated();
      return session;
    })();
    this.refreshingSession = operation;
    const clear = () => {
      if (this.refreshingSession === operation) this.refreshingSession = null;
    };
    void operation.then(clear, clear);
    return operation;
  }

  private async rotateSession(): Promise<Session> {
    const refreshToken = this.session?.refreshToken;
    if (!refreshToken) throw new Error("No refresh token");
    try {
      const session = await this.send<Session>("auth.refresh", { refreshToken });
      this.setSession(session);
      this.authenticatedSocket = null;
      return session;
    } catch (error) {
      if (error instanceof APIError && error.code === "invalid_refresh") this.setSession(null);
      throw error;
    }
  }

  protected async request<T>(
    method: string,
    payload: object = {},
    retried = false,
    opId = "",
  ): Promise<T> {
    if (!this.session) throw new Error("No active session");
    await this.ensureAuthenticated();
    const stableOpId = opId || (isIdempotentMethod(method) ? `op_${randomUUID()}` : "");
    if (stableOpId)
      this.optimisticOperations.set(stableOpId, { method, startedAt: performance.now() });
    try {
      const result = await this.send<T>(method, payload, this.session.accessToken, stableOpId);
      this.observeServerTime(result);
      return result;
    } catch (error) {
      if (!retried && error instanceof APIError && error.code === "unauthorized") {
        await this.refresh();
        return this.request<T>(method, payload, true, stableOpId);
      }
      throw error;
    } finally {
      if (stableOpId) this.optimisticOperations.delete(stableOpId);
    }
  }

  private async requestRunWithResync(method: string, payload: object): Promise<RunView> {
    const opId = `op_${randomUUID()}`;
    try {
      return await this.request<RunView>(method, payload, false, opId);
    } catch (error) {
      if (
        error instanceof APIError &&
        ["state_mismatch", "operation_sequence_invalid", "run_resync_required"].includes(error.code)
      ) {
        await this.resyncRun();
        return this.request<RunView>(method, payload, false, opId);
      }
      throw error;
    }
  }

  private async send<T>(method: string, payload: object = {}, token = "", opId = ""): Promise<T> {
    const socket = await this.ensureConnected();
    const id = (this.nextID++).toString(36);
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        const pending = this.pending.get(id);
        this.pending.delete(id);
        if (pending) this.restorePrediction(pending);
        reject(new APIError("request_timeout", "连接响应超时", 408));
      }, 12_000);
      this.pending.set(id, { resolve: (value) => resolve(value as T), reject, timer });
      try {
        const decisionMs = opId
          ? Math.max(0, Math.min(300_000, Math.round(performance.now() - this.lastAuthoritativeAt)))
          : undefined;
        const securePayload = method === "auth.session" && token ? { accessToken: token } : payload;
        const runOperation = isRunOperationMethod(method);
        const clientTick = opId ? this.serverClock.nextClientTick() : undefined;
        let runPrediction: PendingRequest["runPrediction"];
        if (runOperation && method !== "run.start" && this.runCursor && this.confirmedRun) {
          const action = predictionAction(method, payload);
          if (action) {
            const predicted = predictRunOperation(
              action,
              this.runCursor.operationSeq + 1,
              clientTick ?? this.serverClock.nextClientTick(),
              this.runCursor.stateHash,
            );
            if (predicted) {
              runPrediction = {
                base: this.confirmedRun,
                action,
                operationSeq: this.runCursor.operationSeq + 1,
                preStateHash: this.runCursor.stateHash,
                opId,
              };
              this.runListener?.(predicted.view);
            }
          }
        }
        const pending = this.pending.get(id);
        if (pending) pending.runPrediction = runPrediction;
        void socket
          .sendEnvelope({
            id,
            method,
            opId: opId || undefined,
            decisionMs,
            payload: securePayload,
            clientTickMs: clientTick,
            runId: runOperation && method !== "run.start" ? this.runCursor?.runId : undefined,
            runOpSeq:
              runOperation && method !== "run.start"
                ? (this.runCursor?.operationSeq ?? 0) + 1
                : undefined,
            preStateHash:
              runOperation && method !== "run.start"
                ? hexBytes(this.runCursor?.stateHash ?? "")
                : undefined,
          })
          .catch((error) => {
            window.clearTimeout(timer);
            const failed = this.pending.get(id);
            this.pending.delete(id);
            if (failed) this.restorePrediction(failed);
            reject(error instanceof Error ? error : new Error("Encrypted WebSocket send failed"));
          });
      } catch (error) {
        window.clearTimeout(timer);
        const failed = this.pending.get(id);
        this.pending.delete(id);
        if (failed) this.restorePrediction(failed);
        reject(error instanceof Error ? error : new Error("WebSocket send failed"));
      }
    });
  }

  private ensureConnected(): Promise<SecureSocket> {
    if (this.socket?.isOpen) return Promise.resolve(this.socket);
    if (this.connecting) return this.connecting;

    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    this.connecting = new Promise<SecureSocket>((resolve, reject) => {
      let socket: SecureSocket | null = null;
      let closedBeforeReady = false;
      const closed = () => {
        if (!socket) {
          closedBeforeReady = true;
          return;
        }
        if (this.socket !== socket) return;
        this.socket = null;
        if (this.authenticatedSocket === socket) this.authenticatedSocket = null;
        this.connecting = null;
        window.clearTimeout(this.predictionTimer);
        this.predictionTimer = 0;
        if (
          this.session &&
          this.connectionState !== "offline" &&
          this.connectionState !== "retrying"
        )
          this.setConnectionState("reconnecting");
        else this.setConnectionState("disconnected");
        const error = new APIError("connection_lost", "与游戏服务的连接已断开", 503);
        for (const [id, pending] of this.pending) {
          window.clearTimeout(pending.timer);
          this.restorePrediction(pending);
          pending.reject(error);
          this.pending.delete(id);
        }
        if (!this.restoringSession) this.scheduleReconnect();
      };
      void SecureSocket.connect(
        `${protocol}//${location.host}/ws`,
        (envelope) => this.handleMessage(envelope),
        closed,
      )
        .then((connected) => {
          socket = connected;
          if (closedBeforeReady || !connected.isOpen) {
            this.connecting = null;
            if (
              this.session &&
              this.connectionState !== "offline" &&
              this.connectionState !== "retrying"
            )
              this.setConnectionState("reconnecting");
            else this.setConnectionState("disconnected");
            reject(new APIError("connection_lost", "加密连接在初始化期间关闭", 503));
            return;
          }
          this.socket = connected;
          this.connecting = null;
          resolve(connected);
        })
        .catch((error) => {
          this.connecting = null;
          if (!this.session) this.setConnectionState("disconnected");
          reject(
            error instanceof APIError
              ? error
              : new APIError(
                  "connection_failed",
                  error instanceof Error ? error.message : "无法建立加密连接",
                  503,
                ),
          );
        });
    });
    return this.connecting;
  }

  private handleMessage(envelope: SecureEnvelope) {
    if (envelope.event === "run.state" && envelope.payload) {
      this.lastAuthoritativeAt = performance.now();
      const view = this.observeRun(envelope.payload as RunView, false);
      this.runListener?.(view);
      return;
    }
    if (envelope.event === "run.advance" && envelope.payload) {
      void this.acceptRunAdvance(
        envelope.payload as {
          serverTime: number;
          revision: number;
          lastOpSeq: number;
          stateHash: string;
          randomWindowDelta?: RunView["randomWindow"];
        },
      ).catch(() =>
        this.resyncRun()
          .then((view) => this.runListener?.(view))
          .catch(() => undefined),
      );
      return;
    }
    if (envelope.event === "run.checkpoint" && envelope.payload) {
      const challenge = envelope.payload as {
        checkpointId?: string;
        runId?: string;
        revision?: number;
        lastOpSeq?: number;
      };
      const cursor = this.runCursor;
      if (
        !cursor ||
        !challenge.checkpointId ||
        challenge.runId !== cursor.runId ||
        challenge.revision !== cursor.revision ||
        challenge.lastOpSeq !== cursor.operationSeq
      ) {
        void this.resyncRun()
          .then((view) => this.runListener?.(view))
          .catch(() => undefined);
        return;
      }
      void this.request("run.checkpoint", {
        checkpointId: challenge.checkpointId,
        runId: cursor.runId,
        revision: cursor.revision,
        lastOpSeq: cursor.operationSeq,
        stateHash: cursor.stateHash,
        operationDigest: this.operationDigest || cursor.stateHash,
      }).catch(() =>
        this.resyncRun()
          .then((view) => this.runListener?.(view))
          .catch(() => undefined),
      );
      return;
    }
    if (envelope.event === "run.resync_required") {
      void this.resyncRun()
        .then((view) => this.runListener?.(view))
        .catch(() => undefined);
      return;
    }
    if (envelope.event === "room.state" && envelope.payload) {
      const view = envelope.payload as RoomView;
      this.serverClock.synchronize(view.serverNow);
      this.roomListener?.(view);
      return;
    }
    if (envelope.event === "race.state" && envelope.payload) {
      const view = envelope.payload as RaceView;
      this.serverClock.synchronize(view.serverNow);
      this.raceListener?.(view);
      return;
    }
    if (envelope.event === "time.sync" && envelope.payload) {
      const serverNow = (envelope.payload as { serverNow?: number }).serverNow;
      if (typeof serverNow === "number") this.serverClock.synchronize(serverNow);
      return;
    }
    if (envelope.event === "connection.ready" && envelope.payload) {
      const serverNow = (envelope.payload as { serverNow?: number }).serverNow;
      if (typeof serverNow === "number") this.serverClock.synchronize(serverNow);
      return;
    }
    if (!envelope.id) return;
    const pending = this.pending.get(envelope.id);
    if (!pending) return;
    window.clearTimeout(pending.timer);
    this.pending.delete(envelope.id);
    if (!envelope.ok || envelope.error) {
      const error = envelope.error ?? { code: "request_failed", message: "请求失败", status: 500 };
      this.restorePrediction(pending);
      pending.reject(new APIError(error.code, error.message, error.status));
      return;
    }
    if (isRunAckPayload(envelope.payload)) {
      void this.acceptRunAck(envelope.payload, pending)
        .then((view) => pending.resolve(view))
        .catch((error) =>
          pending.reject(error instanceof Error ? error : new Error("Run acknowledgement failed")),
        );
      return;
    }
    if (envelope.payload && typeof envelope.payload === "object" && "run" in envelope.payload) {
      this.lastAuthoritativeAt = performance.now();
      const view = this.observeRun(envelope.payload as RunView);
      this.runListener?.(view);
      pending.resolve(view);
      return;
    }
    pending.resolve(envelope.payload);
  }

  private scheduleReconnect() {
    if (
      !this.session ||
      this.reconnectTimer ||
      this.reconnecting ||
      this.connectionState === "offline"
    )
      return;
    if (this.automaticReconnectAttempts >= AUTOMATIC_RECONNECT_LIMIT) {
      this.enterOffline();
      return;
    }
    const delay = reconnectDelayMs(this.automaticReconnectAttempts);
    this.automaticReconnectAttempts++;
    this.setConnectionState("reconnecting");
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = 0;
      void this.recoverAfterReconnect().catch(() => undefined);
    }, delay);
  }

  private recoverAfterReconnect(): Promise<void> {
    if (this.reconnecting) return this.reconnecting;
    const operation = this.recoverAfterReconnectOnce();
    this.reconnecting = operation;
    const clear = () => {
      if (this.reconnecting === operation) this.reconnecting = null;
    };
    void operation.then(clear, clear);
    return operation;
  }

  private async recoverAfterReconnectOnce(): Promise<void> {
    try {
      if (browserIsOffline()) throw new APIError("browser_offline", "当前网络不可用", 503);
      await this.ensureConnected();
      const [run, room, race] = await Promise.allSettled([
        this.resumeRun(),
        this.currentRoom(),
        this.currentRace(),
      ]);
      if (run.status === "fulfilled") this.runListener?.(run.value);
      if (room.status === "fulfilled") this.roomListener?.(room.value);
      if (race.status === "fulfilled") this.raceListener?.(race.value);
      const unexpected = [run, room, race].some(
        (result) =>
          result.status === "rejected" &&
          !(
            result.reason instanceof APIError &&
            ["no_active_run", "no_multiplayer_room", "no_active_race"].includes(result.reason.code)
          ),
      );
      if (unexpected) throw new APIError("state_recovery_failed", "连接已建立但状态恢复失败", 503);
      this.automaticReconnectAttempts = 0;
      this.setConnectionState("connected");
    } catch (error) {
      this.enterOffline();
      throw error instanceof Error ? error : new APIError("connection_failed", "无法恢复连接", 503);
    }
  }

  private enterOffline(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = 0;
    }
    if (this.session) this.setConnectionState("offline");
    else this.setConnectionState("disconnected");
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return;
    this.connectionState = state;
    if (state !== "connected") {
      globalThis.clearTimeout(this.predictionTimer);
      this.predictionTimer = 0;
    }
    this.connectionListener?.(state);
  }

  private setSession(session: Session | null): void {
    this.session = session;
    localStorage.removeItem(SESSION_KEY);
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
    if (!session) {
      this.automaticReconnectAttempts = 0;
      if (this.reconnectTimer) {
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = 0;
      }
      this.setConnectionState("disconnected");
    }
    this.listener(session);
  }

  private async acceptSession(session: Session): Promise<Session> {
    this.setSession(session);
    this.authenticatedSocket = null;
    await this.ensureAuthenticated();
    await this.syncTime().catch(() => this.serverClock.now());
    this.automaticReconnectAttempts = 0;
    this.setConnectionState("connected");
    return session;
  }

  private async ensureAuthenticated(): Promise<void> {
    const socket = await this.ensureConnected();
    if (this.authenticatedSocket === socket) return;
    if (this.authenticating) return this.authenticating;
    if (!this.session?.accessToken) throw new Error("No active session");
    const operation = this.authenticateSocket(socket);
    this.authenticating = operation;
    try {
      await operation;
    } finally {
      if (this.authenticating === operation) this.authenticating = null;
    }
  }

  private async authenticateSocket(socket: SecureSocket): Promise<void> {
    let accessToken = this.session?.accessToken;
    if (!accessToken) throw new Error("No active session");
    try {
      await this.send("auth.session", {}, accessToken);
    } catch (error) {
      if (
        !(error instanceof APIError && error.code === "unauthorized") ||
        !this.session?.refreshToken
      )
        throw error;
      const refreshed = await this.rotateSession();
      if (this.socket !== socket || !socket.isOpen)
        throw new APIError("connection_lost", "会话刷新期间连接已断开", 503);
      accessToken = refreshed.accessToken;
      try {
        await this.send("auth.session", {}, accessToken);
      } catch (authError) {
        if (authError instanceof APIError && authError.code === "unauthorized")
          this.setSession(null);
        throw authError;
      }
    }
    if (this.socket !== socket || !socket.isOpen)
      throw new APIError("connection_lost", "身份认证期间连接已断开", 503);
    this.authenticatedSocket = socket;
  }

  private observeRun(incoming: RunView, resetDigest = true): RunView {
    const view = normalizeRunView(incoming);
    this.serverClock.synchronize(view.serverNow);
    this.runCursor = {
      runId: view.run.id,
      operationSeq: view.run.operationSeq ?? 0,
      stateHash: view.run.syncHash || view.run.boardHash,
      revision: view.run.revision,
    };
    this.confirmedRun = view;
    if (resetDigest) this.operationDigest = this.runCursor.stateHash;
    if (view.run.status !== "active") {
      this.runCursor = null;
      this.confirmedRun = view;
      window.clearTimeout(this.predictionTimer);
      this.predictionTimer = 0;
      releaseRunMachine();
      return view;
    }
    confirmRunMachine(view);
    this.schedulePredictionDeadline(view);
    return view;
  }

  private async acceptRunAck(payload: RunAckPayload, pending: PendingRequest): Promise<RunView> {
    if (payload.snapshot) {
      const view = this.observeRun(payload.snapshot);
      this.runListener?.(view);
      return view;
    }
    const prediction = pending.runPrediction;
    if (!prediction || !rollbackRunMachine(prediction.base)) {
      return this.resyncRun();
    }
    const replayed = predictRunOperation(
      prediction.action,
      prediction.operationSeq,
      payload.runAck.acceptedAt,
      prediction.preStateHash,
    );
    const hash = payload.runAck.stateHash.toLowerCase();
    if (
      !replayed ||
      replayed.operationSeq !== payload.runAck.opSeq ||
      replayed.revision !== payload.runAck.revision ||
      replayed.stateHash.toLowerCase() !== hash
    ) {
      this.restorePrediction(pending);
      return this.resyncRun();
    }
    const damageEvent = [...(payload.runAck.events ?? [])]
      .reverse()
      .find((event) => event.type === "damage");
    const damagePayload = damageEvent?.payload;
    const next: RunView = {
      ...replayed.view,
      serverNow: payload.runAck.acceptedAt,
      randomWindow: payload.runAck.randomWindowDelta ?? replayed.view.randomWindow,
      run: {
        ...replayed.view.run,
        lastDamage: typeof damagePayload?.amount === "number" ? damagePayload.amount : undefined,
        lastDamageAt: damageEvent?.atMs,
        lastDamageTarget:
          typeof damagePayload?.target === "string" ? damagePayload.target : undefined,
        lastDamageElement:
          typeof damagePayload?.element === "string"
            ? (damagePayload.element as RunView["run"]["lastDamageElement"])
            : undefined,
        lastElementOutcome:
          typeof damagePayload?.outcome === "string"
            ? (damagePayload.outcome as RunView["run"]["lastElementOutcome"])
            : undefined,
        operationSeq: payload.runAck.opSeq,
        revision: payload.runAck.revision,
        syncHash: hash,
        boardHash: hash,
      },
    };
    if (!confirmRunMachine(next)) return this.resyncRun();
    this.confirmedRun = next;
    this.runCursor = {
      runId: next.run.id,
      operationSeq: payload.runAck.opSeq,
      revision: payload.runAck.revision,
      stateHash: hash,
    };
    this.operationDigest = await digestOperation(
      this.operationDigest || prediction.preStateHash,
      payload.runAck.opSeq,
      prediction.opId,
      hash,
    );
    this.lastAuthoritativeAt = performance.now();
    this.schedulePredictionDeadline(next);
    this.runListener?.(next);
    return next;
  }

  private restorePrediction(pending: PendingRequest): void {
    const base = pending.runPrediction?.base;
    if (!base) return;
    rollbackRunMachine(base);
    this.confirmedRun = base;
    this.runCursor = {
      runId: base.run.id,
      operationSeq: base.run.operationSeq ?? 0,
      revision: base.run.revision,
      stateHash: base.run.syncHash || base.run.boardHash,
    };
    this.schedulePredictionDeadline(base);
    this.runListener?.(base);
  }

  private async acceptRunAdvance(advance: {
    serverTime: number;
    revision: number;
    lastOpSeq: number;
    stateHash: string;
    randomWindowDelta?: RunView["randomWindow"];
  }): Promise<void> {
    const current = this.confirmedRun;
    if (!current || !this.runCursor || advance.lastOpSeq !== this.runCursor.operationSeq)
      throw new Error("run advance cursor mismatch");
    if (!rollbackRunMachine(current)) throw new Error("run advance rollback failed");
    const predicted = advanceRunMachine(advance.serverTime);
    if (
      !predicted ||
      predicted.revision !== advance.revision ||
      predicted.stateHash.toLowerCase() !== advance.stateHash.toLowerCase()
    ) {
      throw new Error("run advance hash mismatch");
    }
    const next: RunView = {
      ...predicted.view,
      serverNow: advance.serverTime,
      randomWindow: advance.randomWindowDelta ?? predicted.view.randomWindow,
      run: {
        ...predicted.view.run,
        revision: advance.revision,
        operationSeq: advance.lastOpSeq,
        syncHash: advance.stateHash,
        boardHash: advance.stateHash,
      },
    };
    if (!confirmRunMachine(next)) throw new Error("run advance confirmation failed");
    this.confirmedRun = next;
    this.runCursor = {
      runId: next.run.id,
      operationSeq: advance.lastOpSeq,
      revision: advance.revision,
      stateHash: advance.stateHash,
    };
    this.serverClock.synchronize(advance.serverTime);
    this.lastAuthoritativeAt = performance.now();
    this.schedulePredictionDeadline(next);
    this.runListener?.(next);
  }

  private async waitForRunUnlock(): Promise<void> {
    const lockedUntil = this.confirmedRun?.run.lockedUntil ?? 0;
    const delay = lockedUntil - this.serverClock.now() + 24;
    if (delay > 0) await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
  }

  private schedulePredictionDeadline(view: RunView): void {
    window.clearTimeout(this.predictionTimer);
    const deadlines = [
      view.run.comboExpiresAt,
      view.run.fogUntil,
      (view.run as RunStateWithTimers).nextBurnAt,
    ].filter((value): value is number => typeof value === "number" && value > 0);
    for (const monster of view.run.monsters ?? (view.run.monster ? [view.run.monster] : [])) {
      if (monster.hp > 0 && monster.nextAttackAt > 0)
        deadlines.push(Math.max(monster.nextAttackAt, view.run.prepUntil, view.run.lockedUntil));
    }
    if (deadlines.length === 0) return;
    const deadline = Math.min(...deadlines);
    const delay = Math.max(0, deadline - this.serverClock.now());
    this.predictionTimer = window.setTimeout(() => {
      this.predictionTimer = 0;
      if (this.connectionState !== "connected") return;
      const predicted = advanceRunMachine(this.serverClock.now());
      if (!predicted) return;
      this.runListener?.(predicted.view);
      this.schedulePredictionDeadline(predicted.view);
    }, delay + 1);
  }

  private observeServerTime(value: unknown): void {
    if (!value || typeof value !== "object") return;
    const serverNow = (value as { serverNow?: unknown }).serverNow;
    if (typeof serverNow === "number") this.serverClock.synchronize(serverNow);
  }
}
